import { useParallelBooking } from '@/api/hooks/useParallelBooking'
import React, { useState } from 'react'
import { useFormikContext } from 'formik';
import { useBookingStore } from '@/stores/bookingStore';
import { areIntervalsOverlapping, endOfWeek, startOfWeek } from 'date-fns';
import styled, { css } from 'styled-components';
import { format } from 'date-fns';
import Grid from '@/components/Grid';
import { ErrorBoundary } from 'react-error-boundary';
import { BookingDate, bookingTypeTranslate } from '@/components/layout/Sidebar/Bookings';
import FormLoader from '@/components/ui/form/FormLoader';
import useResponsive from '@/hooks/useResponsive';
import MobileBookingItem from '@/components/layout/Sidebar/MobileBookingItem';
import { useBookingDialogStore } from './BookingModal';
import { formatToISOTimezone } from '@/utils/helpers/dates.helpers';
import { translate } from '@/i18n';
import CloseIcon from '@/images/CloseIcon.svg'
import UsersIcon from '@/images/Users.svg'
import TimeIcon from '@/images/Time.svg'
import CalendarCheckIcon from '@/images/CalendarCheck.svg'

const ParallelBookings = () => {
    const [show, setShow] = useState(false)
    const week = useBookingStore(state => state.week)

    const weekStart = startOfWeek(week, { weekStartsOn: 1 })
    const weekEnd = endOfWeek(week, { weekStartsOn: 1 })

    const { values } = useFormikContext<any>()
    const bookingId = useBookingDialogStore(state => state.bookingId)
    const { data } = useParallelBooking({ nodeId: values.seat.id, userId: values.user.id, weekStart, weekEnd })

    const parallel = data?.node_booking_view_v2.items.filter(book => book.reason !== 'my_buisy')
        .filter(slot => areIntervalsOverlapping(
            { start: formatToISOTimezone(slot.start), end: formatToISOTimezone(slot.start) },
            { start: weekStart, end: weekEnd }
        )) || []
        
    return (
        <ParallelsWrapper>
            <Header>
                <Period>{translate('bookings-for-period')}
                    <Span>
                        ({format(weekStart, 'dd.MM.yyyy')} - {format(weekEnd, 'dd.MM.yyyy')})
                    </Span>
                </Period>
                <ToggleBtn
                    onClick={(e) => {
                        e.stopPropagation();
                        setShow(prev => !prev);
                    }}
                >
                    <img
                        src={CloseIcon}
                        alt="toggle"
                        style={{
                        width: 26,
                        height: 26,
                        marginLeft: 6,
                        transform: show ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        }}
                    />
                </ToggleBtn>
            </Header>
            <BookingsWrapper $active={show}>
                {parallel.length ? (
                    <CardsGrid>
                        {parallel.map(item => (
                            <ParrallelBook key={item.id} item={item} />
                        ))}
                    </CardsGrid>
                ) : (
                    <div>{translate('no-parallel-bookings')}</div>
                )}
            </BookingsWrapper>
            {/* <FormLoader isLoading={isLoading} /> */}
        </ParallelsWrapper>
    )
}

export default ParallelBookings

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: #4a4a4a;

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }

  &.open svg {
    transform: rotate(180deg);
  }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    font-weight: 500;
`

const Span = styled.span`
    color: #9382FF;
    margin-left: 5px;
`

const ParrallelBook = ({ item }) => {
    const bookingType = item.rec !== '0' ? 'recurrent' : item.end ? 'common' : 'constant'

    return (
        <ItemWrapper>
            <TopRow>
                <Icon>
                    <img
                        src={UsersIcon}
                        alt="users"
                    />   
                </Icon>

               <Name>{item.user_name}</Name>
            </TopRow>
            <PropsWrapper>
                <Property>
                    <Icon>
                        <img
                            src={TimeIcon}
                            alt="users"
                        />   
                    </Icon>
                    <PropValue>{bookingTypeTranslate[bookingType]}</PropValue>
                </Property>
                <Property>
                    <Icon>
                        <img
                            src={CalendarCheckIcon}
                            alt="users"
                        />   
                    </Icon>
                    <PropValue>
                        <ErrorBoundary fallback={<div>{translate('date-display-error')}</div>}>
                            <BookingDate type={bookingType} gap={item.gap} date={{ start: item.start, end: item.end }} />
                        </ErrorBoundary>
                    </PropValue>
                </Property>
            </PropsWrapper>
        </ItemWrapper>
    )
}

const ParallelsWrapper = styled.div`
    padding: 20px 0;
`

const Period = styled.div`
    font-weight: 600;
    font-size: 20px;
    line-height: 20px;
    color: #000000;
    margin-right: 16px;
`

const BookingsWrapper = styled.div<{ $active?: boolean }>`
    margin-top: 20px;
    max-height: 0;
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0, 1, 0, 1);

    ${({ $active }) => $active && css`
        max-height: 9999px;
        height: auto;
        transition: all 0.5s cubic-bezier(1, 0, 1, 0);
    `}
`

const PropName = styled.div`
    max-width: 50px;
    width: 100%;
`

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 25px;
`

const ItemWrapper = styled.div`
    background: #F2F3F4;
    border-radius: 8px;
    padding: 12px 10px;
    display: flex;
    flex-direction: column;
    color: #2F323C;
    font-size: 14px;
    font-weight: 500;
`

const TopRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`

const Icon = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    cursor: pointer;
    font: inherit;
    color: #4a4a4a;

    svg {
        width: 14px;
        height: 14px;
    }
`

const PropsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`
const Property = styled.div`
    display: flex;
    align-items: center;
`
const PropValue = styled.div`
    margin-left: 8px;
`
const Name = styled.div`

`

